import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import PropertyCard from './ui/PropertyCard';
import DataItem from './ui/DataItem';
import { formatValue } from '../../utils/formatters/valueUtils';
import { formatDateBr } from '../../utils/formatters/dateUtils';
import { propertyApi } from '../../services/BaseApiService.jsx';

const getStatusColor = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (!normalized) return 'default';
  if (normalized.includes('sucesso') || normalized.includes('ok') || normalized.includes('regular'))
    return 'success';
  if (
    normalized.includes('erro') ||
    normalized.includes('falha') ||
    normalized.includes('negativa')
  )
    return 'error';
  return 'warning';
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value];
  return [];
};

const mapTipoContribuinte = (value) => {
  const raw = String(value || '').trim();
  if (raw === '1') return 'PJ';
  if (raw === '2') return 'PF';
  return '';
};

const inferTipoPessoa = ({ defaultTipoPessoa, item, cert, contributor }) => {
  const fromTipoContribuinte =
    mapTipoContribuinte(cert?.tipo_contribuinte) || mapTipoContribuinte(item?.tipo_contribuinte);
  if (fromTipoContribuinte) return fromTipoContribuinte;

  const tipoDoc = String(contributor?.tipo || '').toLowerCase();
  if (tipoDoc.includes('cpf')) return 'PF';
  if (tipoDoc.includes('cnpj')) return 'PJ';

  const digits = String(contributor?.documento || '').replace(/\D/g, '');
  if (digits.length === 11) return 'PF';
  if (digits.length === 14) return 'PJ';

  return defaultTipoPessoa || '';
};

const getCndPayload = (propertyData) => {
  const props = propertyData?.properties;
  if (!props || typeof props !== 'object') return null;
  const key = Object.keys(props).find((k) => {
    const lower = k.toLowerCase();
    return lower === 'serpro_cnd' || lower === 'cnd';
  });
  if (!key) return null;
  return props[key];
};

const getContributorInfo = (extra, item) => {
  const contribArray = normalizeToArray(extra?.contribuinte);
  const contrib = contribArray[0] || {};
  return {
    nome: contrib?.nome || item?.contribuinte_consulta || '--',
    documento: contrib?.documento || item?.codigo_identificacao || '',
    tipo: contrib?.tipo || extra?.tipo_documento || '',
  };
};

const getImovelInfo = (extra, item) => {
  const imovelArray = normalizeToArray(extra?.imovel);
  const imovel = imovelArray[0] || {};
  return {
    cib: imovel?.cib || item?.codigo_identificacao || item?.contribuinte_consulta || '--',
    nome: imovel?.nome || '--',
  };
};

const formatDocumentCode = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  const raw = String(value).trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  return raw;
};

const extractCndRows = (records, tipoPessoa) => {
  const rows = [];

  for (const record of normalizeToArray(records)) {
    const status = record?.status;
    const message = record?.message;
    const dataItems = normalizeToArray(record?.data);

    for (const item of dataItems) {
      const dadosCnd = normalizeToArray(item?.dados_cnd);
      for (const cnd of dadosCnd) {
        const certidoes = normalizeToArray(cnd?.certidao);
        const estruturados = normalizeToArray(cnd?.dados_estruturados);

        if (certidoes.length === 0 && estruturados.length === 0) {
          rows.push({
            tipoPessoa: mapTipoContribuinte(item?.tipo_contribuinte) || tipoPessoa || 'PJ',
            titulo: item?.codigo_identificacao || item?.contribuinte_consulta || '--',
            contribuinte: item?.contribuinte_consulta,
            identificacao: item?.contribuinte_consulta,
            documento: item?.codigo_identificacao || item?.contribuinte_consulta,
            dataConsulta: item?.data_consulta,
            status: cnd?.status || status,
            mensagem: cnd?.mensagem || message,
            tipoCertidao: null,
            numeroControle: null,
            dataEmissao: null,
            dataValidade: null,
            pdf: item?.pdf_salvo,
          });
          continue;
        }

        const maxLength = Math.max(certidoes.length, estruturados.length);
        for (let i = 0; i < maxLength; i += 1) {
          const cert = certidoes[i] || {};
          const extra = estruturados[i] || {};
          const contributor = getContributorInfo(extra, item);
          const tipoPessoaEfetivo = inferTipoPessoa({
            defaultTipoPessoa: tipoPessoa,
            item,
            cert,
            contributor,
          });
          const titleIdentifier =
            contributor.documento ||
            item?.codigo_identificacao ||
            item?.contribuinte_consulta ||
            contributor.nome;

          rows.push({
            tipoPessoa: tipoPessoaEfetivo || 'PJ',
            titulo: titleIdentifier,
            contribuinte: contributor.nome,
            identificacao: contributor.nome,
            documento: contributor.documento,
            tipoDocumento: contributor.tipo,
            dataConsulta: item?.data_consulta,
            status: cnd?.status || status,
            mensagem: cnd?.mensagem || message,
            tipoCertidao: cert?.tipo_certidao || extra?.tipo_documento,
            numeroControle:
              extra?.numero_controle || cert?.codigo_controle || extra?.codigo_verificacao,
            dataEmissao: cert?.data_emissao || extra?.data_emissao,
            dataValidade: cert?.data_validade || extra?.validade,
            pdf: cert?.pdf_salvo_em || item?.pdf_salvo,
          });
        }
      }
    }
  }

  const dedupe = new Map();
  for (const row of rows) {
    const key = [
      row.tipoPessoa,
      row.documento,
      row.numeroControle,
      row.dataEmissao,
      row.tipoCertidao,
    ]
      .map((v) =>
        String(v || '')
          .trim()
          .toLowerCase(),
      )
      .join('|');
    if (!dedupe.has(key)) dedupe.set(key, row);
  }

  return Array.from(dedupe.values());
};

const extractRuralRows = (records) => {
  const rows = [];

  for (const record of normalizeToArray(records)) {
    const status = record?.status;
    const message = record?.message;
    const dataItems = normalizeToArray(record?.data);

    for (const item of dataItems) {
      const dadosCnd = normalizeToArray(item?.dados_cnd);
      for (const cnd of dadosCnd) {
        const certidoes = normalizeToArray(cnd?.certidao);
        const estruturados = normalizeToArray(cnd?.dados_estruturados);

        if (certidoes.length === 0 && estruturados.length === 0) {
          rows.push({
            tipoPessoa: 'RURAL',
            titulo: item?.codigo_identificacao || item?.contribuinte_consulta || '--',
            contribuinte: item?.contribuinte_consulta,
            identificacao: '--',
            documento: item?.codigo_identificacao || item?.contribuinte_consulta,
            identificadorArquivo: item?.contribuinte_consulta || item?.codigo_identificacao,
            dataConsulta: item?.data_consulta,
            status: cnd?.status || status,
            mensagem: cnd?.mensagem || message,
            tipoCertidao: null,
            numeroControle: null,
            dataEmissao: null,
            dataValidade: null,
            pdf: item?.pdf_salvo,
          });
          continue;
        }

        const maxLength = Math.max(certidoes.length, estruturados.length);
        for (let i = 0; i < maxLength; i += 1) {
          const cert = certidoes[i] || {};
          const extra = estruturados[i] || {};
          const contributor = getContributorInfo(extra, item);
          const imovel = getImovelInfo(extra, item);

          rows.push({
            tipoPessoa: 'RURAL',
            titulo: imovel.cib,
            contribuinte: contributor.nome,
            identificacao: imovel.nome,
            documento:
              item?.codigo_identificacao || item?.contribuinte_consulta || contributor.documento,
            identificadorArquivo:
              item?.contribuinte_consulta || item?.codigo_identificacao || contributor.documento,
            tipoDocumento: contributor.tipo,
            dataConsulta: item?.data_consulta,
            status: cnd?.status || status,
            mensagem: cnd?.mensagem || message,
            tipoCertidao: cert?.tipo_certidao || extra?.tipo_documento,
            numeroControle:
              extra?.numero_controle || cert?.codigo_controle || extra?.codigo_verificacao,
            dataEmissao: cert?.data_emissao || extra?.data_emissao,
            dataValidade: cert?.data_validade || extra?.validade,
            pdf: cert?.pdf_salvo_em || item?.pdf_salvo,
          });
        }
      }
    }
  }

  const dedupe = new Map();
  for (const row of rows) {
    const key = [row.titulo, row.numeroControle, row.dataEmissao, row.tipoCertidao]
      .map((v) =>
        String(v || '')
          .trim()
          .toLowerCase(),
      )
      .join('|');
    if (!dedupe.has(key)) dedupe.set(key, row);
  }

  return Array.from(dedupe.values());
};

const normalizeDocFilename = (fileId, rawValue) => {
  if (!rawValue || typeof rawValue !== 'string') return '';
  let value = rawValue.trim().replace(/\\/g, '/');
  if (!value) return '';

  value = value.split('?')[0].split('#')[0];

  const docsToken = '/docs/';
  const docsIdx = value.toLowerCase().indexOf(docsToken);
  if (docsIdx >= 0) {
    const afterDocs = value.slice(docsIdx + docsToken.length);
    const fileIdPrefix = `${String(fileId || '').toLowerCase()}/`;
    if (afterDocs.toLowerCase().startsWith(fileIdPrefix)) {
      value = afterDocs.slice(fileIdPrefix.length);
    } else {
      value = afterDocs;
    }
  }

  value = value.replace(/^\/+/, '');
  if (fileId && value.toLowerCase().startsWith(`${String(fileId).toLowerCase()}/`)) {
    value = value.slice(String(fileId).length + 1);
  }

  return value;
};

const normalizeIdentifierForFilename = (value) => {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const digitsOnly = raw.replace(/\D/g, '');
  return digitsOnly || raw.replace(/\s+/g, '');
};

const buildExpectedCndFilename = (fileId, identifier) => {
  const normalizedFileId = String(fileId || '').trim();
  const normalizedIdentifier = normalizeIdentifierForFilename(identifier);
  if (!normalizedFileId || !normalizedIdentifier) return '';
  return `${normalizedFileId}.${normalizedIdentifier}.cnd.pdf`;
};

const resolveCndFilename = (fileId, row) => {
  const fromPdfField = normalizeDocFilename(fileId, row?.pdf);
  if (fromPdfField && fromPdfField.toLowerCase().endsWith('.pdf')) return fromPdfField;

  const fromSpecificIdentifier = buildExpectedCndFilename(fileId, row?.identificadorArquivo);
  if (fromSpecificIdentifier) return fromSpecificIdentifier;

  const fromDocumento = buildExpectedCndFilename(fileId, row?.documento);
  if (fromDocumento) return fromDocumento;

  const fromIdentificacao = buildExpectedCndFilename(fileId, row?.identificacao);
  if (fromIdentificacao) return fromIdentificacao;

  return buildExpectedCndFilename(fileId, row?.contribuinte);
};

const buildDocUrl = (fileId, filename) => {
  if (!fileId || !filename) return '';
  const encodedPath = String(filename)
    .split('/')
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  if (!encodedPath) return '';
  const base = propertyApi?.defaults?.baseURL || '/api/v1';
  return `${base}/docs/${encodeURIComponent(String(fileId))}/${encodedPath}`;
};

const CndTab = ({ propertyData }) => {
  const rawFileId =
    propertyData?._metadata?.file_id ||
    propertyData?._contextData?.filename ||
    propertyData?._contextData?.accepted_file_id ||
    '';
  const fileId =
    typeof rawFileId === 'string' && rawFileId.toLowerCase().endsWith('.json')
      ? rawFileId.slice(0, -5)
      : rawFileId;

  const parsed = useMemo(() => {
    const payload = getCndPayload(propertyData);
    const envelopes = normalizeToArray(payload);

    const pfRecords = [];
    const pjRecords = [];
    const ruralRecords = [];

    for (const envelope of envelopes) {
      pfRecords.push(...normalizeToArray(envelope?.pf));
      pjRecords.push(...normalizeToArray(envelope?.pj));
      ruralRecords.push(...normalizeToArray(envelope?.imovel_rural));
    }

    return {
      pfRows: extractCndRows(pfRecords, 'PF'),
      pjRows: extractCndRows(pjRecords, 'PJ'),
      ruralRows: extractRuralRows(ruralRecords),
    };
  }, [propertyData]);

  const total = parsed.pfRows.length + parsed.pjRows.length + parsed.ruralRows.length;

  if (total === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200 text-gray-600">
        Nenhum dado de CND disponível para esta propriedade.
      </div>
    );
  }

  const renderRows = (rows) => {
    if (rows.length === 0) {
      return <div className="text-gray-500">Sem registros.</div>;
    }

    return (
      <div className="space-y-3">
        {rows.map((row, index) => {
          const normalizedFilename = resolveCndFilename(fileId, row);
          const docUrl = buildDocUrl(fileId, normalizedFilename);

          return (
            <div
              key={`${row.tipoPessoa}-${index}`}
              className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-sm font-semibold text-gray-800">
                  {formatDocumentCode(row.titulo || row.documento)}
                </div>
                <Chip
                  size="small"
                  color={getStatusColor(row.status)}
                  label={formatValue(row.status)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <DataItem label="Identificação" value={formatValue(row.identificacao)} isCopyable />
                <DataItem label="Tipo certidão" value={formatValue(row.tipoCertidao)} />
                <DataItem
                  label="Número controle"
                  value={formatValue(row.numeroControle)}
                  isCopyable
                />
                <DataItem label="Data emissão" value={formatDateBr(row.dataEmissao) || '--'} />
                <DataItem label="Data validade" value={formatDateBr(row.dataValidade) || '--'} />
                <DataItem label="Data consulta" value={formatDateBr(row.dataConsulta) || '--'} />
                <div className="flex flex-col mb-2">
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <span>PDF</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {docUrl ? (
                      <a
                        href={docUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-medium text-blue-700 hover:text-blue-800 underline"
                      >
                        Abrir PDF
                      </a>
                    ) : (
                      <span className="font-medium text-sm text-gray-900">
                        {row.pdf ? 'Disponível' : 'Não informado'}
                      </span>
                    )}
                  </div>
                </div>
                <DataItem label="Mensagem" value={formatValue(row.mensagem)} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Box className="space-y-4">
      <PropertyCard
        title="Certidão Negativa de Débitos (CND)"
        subtitle="Dados SERPRO CND"
        color="purple"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <DataItem label="Total de registros" value={String(total)} highlight />
          <DataItem label="Pessoa Física" value={String(parsed.pfRows.length)} />
          <DataItem label="Pessoa Jurídica" value={String(parsed.pjRows.length)} />
          <DataItem label="Imóvel Rural" value={String(parsed.ruralRows.length)} />
        </div>
      </PropertyCard>

      <Accordion disableGutters defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>CND - Pessoas Físicas (PF)</Typography>
        </AccordionSummary>
        <AccordionDetails>{renderRows(parsed.pfRows)}</AccordionDetails>
      </Accordion>

      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>CND - Pessoas Jurídicas (PJ)</Typography>
        </AccordionSummary>
        <AccordionDetails>{renderRows(parsed.pjRows)}</AccordionDetails>
      </Accordion>

      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>CND - Imóvel Rural</Typography>
        </AccordionSummary>
        <AccordionDetails>{renderRows(parsed.ruralRows)}</AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CndTab;
